import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@safetyhub/ui';
import { authApi } from '../lib/api';
import { useAuthStore } from '../store/auth';

// 에러 코드별 사용자 친화적 메시지
const ERROR_MESSAGES: Record<string, string> = {
  AUTH_LOGIN_INVALID_CREDENTIALS: '아이디 또는 비밀번호가 올바르지 않습니다.',
  AUTH_LOGIN_NOT_SUPER_ADMIN: '슈퍼관리자만 로그인할 수 있습니다.',
  PERMISSION_IP_NOT_ALLOWED: '허용되지 않은 IP 주소입니다. 관리자에게 문의하세요.',
};

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Admin 전용 로그인 API 호출
      // 백엔드에서 isSuperAdmin 검증 수행
      const response = await authApi.login(identifier, password);

      if (!response.success || !response.data) {
        // 에러 코드에 따른 메시지 표시
        const errorCode = response.error?.code || '';
        const errorMessage = ERROR_MESSAGES[errorCode] || response.error?.message || '로그인에 실패했습니다.';
        setError(errorMessage);
        return;
      }

      const { id, email, name, tokens } = response.data;

      // 로그인 성공 - 백엔드에서 이미 isSuperAdmin 검증됨
      login(
        {
          id,
          email,
          name,
          isSuperAdmin: true, // Admin API로 로그인 성공 = 슈퍼관리자 확정
        },
        tokens.accessToken,
        tokens.refreshToken
      );

      navigate('/');
    } catch {
      setError('아이디 또는 비밀번호를 확인해 주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="bg-white p-8 rounded-card-lg shadow-xl w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">SafetyHub Admin</h1>
          <p className="text-sm text-red-600 mt-2">슈퍼관리자 전용</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="identifier" className="block text-sm font-medium text-gray-700">
              이메일 또는 아이디
            </label>
            <input
              type="text"
              id="identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoComplete="username"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-control shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              비밀번호
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-control shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <Button type="submit" variant="primary" size="lg" disabled={loading} className="w-full">
            {loading ? '로그인 중...' : '로그인'}
          </Button>

          <div className="text-center">
            <Link
              to="/password-reset"
              className="text-sm text-gray-600 hover:text-red-600"
            >
              비밀번호를 잊으셨나요?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
